import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type { Provider } from '@/types/core/provider';
import type { Page } from '@/types/shared/page';
import { isNilUUID, type UUID } from '@/utils/uuid';

import { providerQueryKeys } from './query-keys';

/**
 * List providers for a given tenant.
 * Accepts optional paging args via `opts`.
 */
export function useListProviders(
  tenantId: UUID,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
    enabled?: boolean;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Provider>, unknown>, Error> {
  return useInfiniteQuery<
    Page<Provider>,
    Error,
    InfiniteData<Page<Provider>, unknown>
  >({
    queryKey: providerQueryKeys.list(tenantId, opts?.limit, opts?.filter),
    queryFn: (context: QueryFunctionContext) => {
      const pageParam = context.pageParam as string | undefined;
      return providerService.listProviders(tenantId, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    enabled: (opts?.enabled ?? true) && !isNilUUID(tenantId),
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
